import { Link } from "react-router-dom";
import { AlertCircle, Check, CheckCircle2, ShieldCheck } from "lucide-react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { CITIES, REGIONS } from "../../lib/options";

export const slug = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const Req = () => <span className="text-[#B25B41] ml-0.5" aria-hidden="true">*</span>;

export const Field = ({ label, htmlFor, hint, required, children }) => (
  <div className="space-y-2">
    <label htmlFor={htmlFor} className="block text-sm font-semibold text-[#1C2421]">{label}{required && <Req />}</label>
    {hint && <p className="text-xs text-[#5A6561]">{hint}</p>}
    {children}
  </div>
);

export const Group = ({ legend, hint, required, children }) => (
  <fieldset className="space-y-3">
    <legend className="text-sm font-semibold text-[#1C2421]">{legend}{required && <Req />}</legend>
    {hint && <p className="text-xs text-[#5A6561]">{hint}</p>}
    {children}
  </fieldset>
);

export const TextField = ({ id, label, value, onChange, required, hint, type = "text", testId, ...rest }) => (
  <Field label={label} htmlFor={id} hint={hint} required={required}>
    <Input id={id} type={type} value={value} onChange={(e) => onChange(e.target.value)} className="h-11 bg-white"
      required={required} data-testid={testId} {...rest} />
  </Field>
);

export const TextArea = ({ id, label, value, onChange, required, hint, max = 1500, testId, rows = 4 }) => (
  <Field label={label} htmlFor={id} hint={hint} required={required}>
    <Textarea id={id} value={value} rows={rows} maxLength={max} onChange={(e) => onChange(e.target.value)}
      className="bg-white" data-testid={testId} />
    <p className="text-[11px] text-[#5A6561] text-right">{value.length}/{max}</p>
  </Field>
);

export const NativeSelect = ({ id, value, onChange, options, placeholder = "Select an option", testId }) => (
  <select id={id} value={value} onChange={(e) => onChange(e.target.value)} className="field-select" data-testid={testId}>
    <option value="">{placeholder}</option>
    {options.map((o) => <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>)}
  </select>
);

export const ChoiceCards = ({ options, value, onChange, testId, cols = "sm:grid-cols-2" }) => (
  <div className={`grid grid-cols-1 ${cols} gap-3`} role="radiogroup">
    {options.map((o) => {
      const v = o.value ?? o;
      const active = value === v;
      return (
        <button type="button" key={v} role="radio" aria-checked={active} onClick={() => onChange(v)}
          className={`choice ${active ? "choice-active" : ""}`} data-testid={`${testId}-${slug(v)}`}>
          <span className={`mt-0.5 h-4 w-4 shrink-0 rounded-full border grid place-items-center ${active ? "border-[#1E3A2B] bg-[#1E3A2B]" : "border-[#CFC8BC]"}`}>
            {active && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
          </span>
          <span>
            <span className="block font-medium">{o.label ?? o}</span>
            {o.desc && <span className="block text-xs text-[#5A6561] mt-0.5">{o.desc}</span>}
          </span>
        </button>
      );
    })}
  </div>
);

export const MultiChips = ({ options, value, onChange, testId }) => {
  const toggle = (v) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  return (
    <div className="flex flex-wrap gap-2.5">
      {options.map((o) => {
        const active = value.includes(o);
        return (
          <button type="button" key={o} aria-pressed={active} onClick={() => toggle(o)}
            className={`chip ${active ? "chip-active" : ""}`} data-testid={`${testId}-${slug(o)}`}>
            {active && <Check className="h-3.5 w-3.5" aria-hidden="true" />}{o}
          </button>
        );
      })}
    </div>
  );
};

export const LocationFields = ({ form, set, prefix, regions = REGIONS, legend = "Where is support needed?" }) => {
  const cities = CITIES[form.region] || [];
  const showCustom = form.region === "other" || form.city === "Other community";
  return (
    <div className="space-y-6">
      <Group legend={legend} required>
        <ChoiceCards options={regions} value={form.region} cols="sm:grid-cols-2" testId={`${prefix}-region`}
          onChange={(v) => { set("region", v); set("city", ""); }} />
      </Group>
      {cities.length > 0 && (
        <Field label="City or community" htmlFor={`${prefix}-city`} required>
          <NativeSelect id={`${prefix}-city`} value={form.city} onChange={(v) => set("city", v)} options={cities}
            placeholder="Select a city or community" testId={`${prefix}-input-city`} />
        </Field>
      )}
      {showCustom && (
        <TextField id={`${prefix}-city-custom`} label={form.region === "other" ? "City, province/state and country" : "Community name"}
          value={form.city_custom} onChange={(v) => set("city_custom", v)} required testId={`${prefix}-input-city-custom`} />
      )}
      <p className="text-xs text-[#5A6561]">Sharing your location helps us understand your request. It does not confirm service coverage in a specific community; we will discuss what may be possible in your area.</p>
    </div>
  );
};

export const ContactFields = ({ form, set, prefix, preferred = true }) => (
  <div className="grid sm:grid-cols-2 gap-5">
    <TextField id={`${prefix}-name`} label="Full name" value={form.name} onChange={(v) => set("name", v)} required autoComplete="name" testId={`${prefix}-input-name`} />
    <TextField id={`${prefix}-email`} label="Email" type="email" value={form.email} onChange={(v) => set("email", v)} required autoComplete="email" testId={`${prefix}-input-email`} />
    <TextField id={`${prefix}-phone`} label="Phone (optional)" type="tel" value={form.phone} onChange={(v) => set("phone", v)} autoComplete="tel" testId={`${prefix}-input-phone`} />
    {preferred && (
      <Field label="Preferred contact method" htmlFor={`${prefix}-preferred`}>
        <NativeSelect id={`${prefix}-preferred`} value={form.preferred_contact} onChange={(v) => set("preferred_contact", v)}
          options={["Email", "Phone call", "Text message", "No preference"]} placeholder="No preference" testId={`${prefix}-input-preferred-contact`} />
      </Field>
    )}
  </div>
);

const ConsentRow = ({ id, checked, onChange, children, testId }) => (
  <div className="flex items-start gap-3">
    <Checkbox id={id} checked={checked} onCheckedChange={(v) => onChange(v === true)} className="mt-0.5 h-[18px] w-[18px] rounded-[4px] bg-white" data-testid={testId} />
    <label htmlFor={id} className="text-sm text-[#1C2421] leading-relaxed cursor-pointer">{children}</label>
  </div>
);

export const ConsentBlock = ({ form, set, prefix, purpose = "inquiry", children }) => (
  <div className="rounded-2xl border border-[#E5E0D8] bg-[#F3EFEA]/60 p-5 sm:p-6 space-y-4" data-testid={`${prefix}-consent-block`}>
    <p className="flex items-center gap-2 text-sm font-semibold text-[#1E3A2B]"><ShieldCheck className="h-4 w-4" /> Privacy and consent</p>
    <p className="text-xs text-[#5A6561] leading-relaxed">
      We collect only the information needed to respond to your {purpose}. It is stored securely, reviewed only by authorized staff, and is never sold. Please do not include detailed medical histories, health card numbers, Social Insurance Numbers or banking details. Read our <Link to="/privacy" className="underline text-[#1E3A2B]" data-testid={`${prefix}-privacy-link`}>Privacy Notice</Link> to learn about your privacy rights.
    </p>
    {children}
    <ConsentRow id={`${prefix}-privacy`} checked={form.privacy_consent} onChange={(v) => set("privacy_consent", v)} testId={`${prefix}-input-privacy-consent`}>
      I have read the Privacy Notice and consent to Friendship Pen Care Services collecting and using this information to respond to my {purpose}.<span className="text-[#B25B41]"> *</span>
    </ConsentRow>
    <ConsentRow id={`${prefix}-marketing`} checked={form.marketing_consent} onChange={(v) => set("marketing_consent", v)} testId={`${prefix}-input-marketing-consent`}>
      Optional: I would like to receive occasional updates about Friendship Pen Care Services. I can unsubscribe at any time.
    </ConsentRow>
  </div>
);

export { ConsentRow };

export const Honeypot = ({ value, onChange }) => (
  <div className="hp-field" aria-hidden="true">
    <label htmlFor="website-field">Leave this field empty</label>
    <input id="website-field" name="website" tabIndex={-1} autoComplete="off" value={value} onChange={(e) => onChange(e.target.value)} />
  </div>
);

export const FormError = ({ message, testId = "form-error" }) =>
  message ? (
    <div role="alert" className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" data-testid={testId}>
      <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />{message}
    </div>
  ) : null;

export const SuccessPanel = ({ reference, title, children, onReset, testId, resetLabel = "Submit another inquiry" }) => (
  <div className="card-warm p-8 sm:p-12 text-center animate-step" data-testid={testId} role="status">
    <CheckCircle2 className="h-12 w-12 text-[#1E3A2B] mx-auto" aria-hidden="true" />
    <h3 className="font-display text-2xl sm:text-3xl font-semibold text-[#1E3A2B] mt-5">{title}</h3>
    <p className="text-sm text-[#5A6561] mt-3">Your reference number</p>
    <p className="font-mono text-lg font-semibold text-[#C86D51] mt-1" data-testid={`${testId}-reference`}>{reference}</p>
    <div className="text-sm text-[#5A6561] leading-relaxed max-w-lg mx-auto mt-5 space-y-3">{children}</div>
    {onReset && <button type="button" onClick={onReset} className="btn-pill btn-outline mt-8" data-testid={`${testId}-reset-button`}>{resetLabel}</button>}
  </div>
);
